// ANALYTICS-01 Today widgets: a fixed catalogue, each backed by a report endpoint; the user adds,
// removes and reorders them, and the layout is saved per user (PUT /orgs/{o}/dashboard-layout).
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "@ady/i18n";
import { Button, EmptyState, Skeleton, useToast } from "@ady/ui";
import { LoadError } from "../settings/SettingsPage";
import { TodayKpis } from "../orders/TodayKpis";
import { Card, useCan, useStoreIds } from "../orders/ui";
import {
  WIDGET_IDS,
  analyticsApi,
  analyticsKeys,
  useLayout,
  useLeaderboard,
  useReport,
  type BreakdownRow,
  type Report,
  type Topic,
  type WidgetId,
} from "./api";
import {
  addDays,
  addWidget,
  formatFigure,
  isPlanLocked,
  keyLabel,
  metricLabel,
  moveWidget,
  nptToday,
  removeWidget,
} from "./format";
import { ATID, LeaderboardBand, PlanLocked, ReportBody, ReportFigures } from "./ReportView";

const SOURCE: Readonly<
  Record<WidgetId, { topic: Topic; dim?: "product" | "method" | "courier" } | null>
> = {
  kpis: null,
  sales_trend: { topic: "sales" },
  collected_outstanding: { topic: "sales" },
  top_products: { topic: "products", dim: "product" },
  payment_methods: { topic: "payments", dim: "method" },
  courier_performance: { topic: "delivery", dim: "courier" },
  returns: { topic: "returns" },
  traffic_funnel: { topic: "traffic" },
  leaderboard: null,
};

function ReportWidget({ id }: { id: WidgetId }) {
  const { orgId, storeId } = useStoreIds();
  const src = SOURCE[id]!;
  const today = nptToday(Date.now());
  const q = useReport(orgId, storeId, src.topic, {
    from: addDays(today, -6),
    to: today,
    ...(src.dim ? { dim: src.dim } : {}),
  });
  if (q.isError)
    return isPlanLocked(q.error) ? (
      <PlanLocked />
    ) : (
      <LoadError error={q.error} onRetry={() => void q.refetch()} />
    );
  if (!q.data) return <Skeleton className="h-20 w-full" />;
  if (id === "collected_outstanding") return <CollectedOutstanding report={q.data} />;
  if (id === "sales_trend") return <SalesWeek report={q.data} />;
  if (src.dim) return <Ranked report={q.data} />;
  return <ReportBody report={q.data} compact />;
}

/** The figure each ranked widget sorts and draws its bars by. */
const RANK_BY: Readonly<Record<string, string>> = {
  product: "gross_paisa",
  method: "net_paisa",
  courier: "shipments",
};

/**
 * Top products, payment methods and couriers (owner, 2026-11-01: compact like "Sales this week"):
 * three headline figures, then the top four as one line each with a thin bar (no tall row cards).
 */
function Ranked({ report }: { report: Report }) {
  const empty = Object.values(report.totals).every((v) => !v) && report.rows.length === 0;
  if (report.availability === "activation_required" || empty)
    return <ReportBody report={report} compact />;
  const by =
    RANK_BY[report.dim] ??
    Object.keys(report.rows[0] ?? {}).find((k) => k !== "key" && k !== "label") ??
    "";
  const value = (r: BreakdownRow) => Number(r[by] ?? 0) || 0;
  const top = [...report.rows].sort((a, b) => value(b) - value(a)).slice(0, 4);
  const max = Math.max(1, ...top.map(value));
  return (
    <div className="flex flex-col gap-3">
      <ReportFigures report={report} limit={3} />
      {top.length ? (
        <div className="flex flex-col gap-2">
          <p className="flex justify-between text-2xs text-fg-tertiary">
            <span>{t(`analytics.dims.${report.dim}`)}</span>
            <span>{metricLabel(by)}</span>
          </p>
          <ol className="flex flex-col gap-2">
            {top.map((r) => (
              <li
                key={r.key}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1"
              >
                <span className="truncate text-xs" data-content="user">
                  {keyLabel(report.dim, r)}
                </span>
                <span className="ady-ledger text-xs font-semibold">
                  {formatFigure(by, value(r))}
                </span>
                <span
                  className="col-span-2 h-1.5 overflow-hidden rounded-full bg-sunken"
                  aria-hidden="true"
                >
                  <span
                    className="block h-full rounded-full ady-bg-brand"
                    style={{ width: `${Math.max(Math.round((value(r) / max) * 100), 2)}%` }}
                  />
                </span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

const dayLabel = new Intl.DateTimeFormat("en", { day: "numeric", month: "short", timeZone: "UTC" });

/**
 * "Sales this week" (owner, 2026-11-01: compact, level with "Today at a glance"): the three headline
 * figures, then one short bar per day (net sales) with its amount and date; the full day line
 * (orders, average, collected) is the bar's tooltip and its accessible name.
 */
function SalesWeek({ report }: { report: Report }) {
  if (report.availability === "activation_required") return <ReportBody report={report} compact />;
  const days = report.series.slice(-7);
  const net = (d: (typeof days)[number]) => Number(d.net_paisa ?? 0) || 0;
  const max = Math.max(1, ...days.map(net));
  return (
    <div className="flex flex-col gap-3">
      <ReportFigures report={report} limit={3} />
      <ol
        className="grid h-37 grid-cols-7 items-end gap-1.5"
        aria-label={t("analytics.widgets.names.sales_trend")}
      >
        {days.map((d) => {
          const v = net(d);
          const line = [
            dayLabel.format(new Date(`${d.date}T00:00:00Z`)),
            `${metricLabel("net_paisa")} ${formatFigure("net_paisa", v)}`,
            `${metricLabel("orders")} ${formatFigure("orders", d.orders)}`,
            `${metricLabel("collected_paisa")} ${formatFigure("collected_paisa", d.collected_paisa)}`,
          ].join(" · ");
          return (
            <li
              key={d.date}
              title={line}
              aria-label={line}
              className="flex h-full min-w-0 flex-col items-center justify-end gap-1"
            >
              <span className="ady-ledger max-w-full truncate text-2xs text-fg-secondary">
                {formatFigure("net_paisa", v)}
              </span>
              <span
                aria-hidden="true"
                className={v ? "w-full rounded-xs ady-bg-brand" : "w-full rounded-xs bg-sunken"}
                style={{ height: `${Math.max(Math.round((v / max) * 100), 4)}%` }}
              />
              <span className="text-2xs whitespace-nowrap text-fg-tertiary" aria-hidden="true">
                {dayLabel.format(new Date(`${d.date}T00:00:00Z`))}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * Collected and still to collect (owner, 2026-11-01: compact like "Sales this week"): the four
 * figures, then one bar split into collected, still to collect and refunded, with a legend.
 */
function CollectedOutstanding({ report }: { report: Report }) {
  const only = { ...report, topic: "payments" as const };
  const parts = (
    [
      ["collected_paisa", "bg-success-fg"],
      ["outstanding_paisa", "bg-warning-fg"],
      ["refunded_paisa", "bg-danger-fg"],
    ] as const
  ).map(([m, tone]) => ({ m, tone, v: Math.max(Number(report.totals[m] ?? 0) || 0, 0) }));
  const whole = parts.reduce((n, p) => n + p.v, 0);
  return (
    <div className="flex flex-col gap-3">
      <ReportFigures report={only} limit={4} />
      {whole > 0 ? (
        <div className="flex flex-col gap-2">
          <div className="flex h-2.5 overflow-hidden rounded-full bg-sunken" aria-hidden="true">
            {parts.map((p) =>
              p.v ? (
                <span key={p.m} className={p.tone} style={{ width: `${(p.v / whole) * 100}%` }} />
              ) : null,
            )}
          </div>
          <p className="flex justify-between text-2xs text-fg-tertiary">
            <span>{t("analytics.widgets.ofNet")}</span>
            <span>{t("analytics.widgets.amountShare")}</span>
          </p>
          <ol className="flex flex-col gap-2">
            {parts.map((p) => (
              <li
                key={p.m}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1"
              >
                <span className="flex items-center gap-1.5 text-xs">
                  <span className={`size-2 rounded-full ${p.tone}`} aria-hidden="true" />
                  {metricLabel(p.m)}
                </span>
                <span className="ady-ledger text-xs font-semibold">
                  {formatFigure(p.m, p.v)}
                  <span className="ps-1.5 font-normal text-fg-tertiary">
                    {Math.round((p.v / whole) * 100)}%
                  </span>
                </span>
                <span
                  className="col-span-2 h-1.5 overflow-hidden rounded-full bg-sunken"
                  aria-hidden="true"
                >
                  <span
                    className={`block h-full rounded-full ${p.tone}`}
                    style={{ width: `${Math.max((p.v / whole) * 100, 2)}%` }}
                  />
                </span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

function LeaderboardWidget() {
  const { orgId } = useStoreIds();
  const q = useLeaderboard(orgId);
  if (!q.data) return <Skeleton className="h-16 w-full" />;
  return <LeaderboardBand view={q.data} canChange={false} />;
}

export function WidgetBody({ id }: { id: WidgetId }) {
  if (id === "kpis") return <TodayKpis />;
  if (id === "leaderboard") return <LeaderboardWidget />;
  return <ReportWidget id={id} />;
}

/** The Today widget area (mounted by pages/Today.tsx). */
export function TodayWidgets() {
  const { orgId } = useStoreIds();
  const allowed = useCan("reports.read");
  const q = useLayout(orgId);
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<WidgetId[] | null>(null);
  const save = useMutation({
    mutationFn: (w: WidgetId[]) => analyticsApi.saveLayout(orgId, w),
    onSuccess: (v) => {
      qc.setQueryData(analyticsKeys.layout(orgId), v);
      setEditing(null);
      toast.show({ tone: "success", title: t("analytics.widgets.saved") });
    },
  });
  if (!allowed) return null;
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton className="h-24 w-full" />;
  const widgets = editing ?? q.data.widgets;
  return (
    <section
      className="flex flex-col gap-3"
      data-testid={ATID.widgets}
      aria-labelledby="analytics-widgets-h"
    >
      <header className="flex items-center justify-between gap-3">
        <h2 id="analytics-widgets-h" className="text-title-sm font-semibold">
          {t("analytics.widgets.title")}
        </h2>
        {editing ? (
          <Button size="sm" loading={save.isPending} onClick={() => save.mutate(editing)}>
            {t("analytics.widgets.done")}
          </Button>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => setEditing([...q.data.widgets])}>
            {t("analytics.widgets.customize")}
          </Button>
        )}
      </header>
      {widgets.length === 0 ? (
        <EmptyState size="inline" headingLevel={3} title={t("analytics.widgets.empty")} />
      ) : null}
      {/* Owner, 2026-11-01: cards side by side end on one line (rows stretch, cards fill them). */}
      <div className="grid gap-4 lg:grid-cols-2">
        {widgets.map((id, i) => (
          <div
            key={id}
            data-testid={ATID.widget}
            data-widget={id}
            className="flex flex-col [&>section]:flex-1"
          >
            <Card
              id={`widget-${id}`}
              title={t(`analytics.widgets.names.${id}`)}
              {...(editing
                ? {
                    aside: (
                      <span className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={i === 0}
                          onClick={() => setEditing(moveWidget(editing, id, -1))}
                        >
                          {t("analytics.widgets.moveUp")}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={i === widgets.length - 1}
                          onClick={() => setEditing(moveWidget(editing, id, 1))}
                        >
                          {t("analytics.widgets.moveDown")}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing(removeWidget(editing, id))}
                        >
                          {t("analytics.widgets.remove")}
                        </Button>
                      </span>
                    ),
                  }
                : {})}
            >
              {editing ? null : <WidgetBody id={id} />}
            </Card>
          </div>
        ))}
      </div>
      {editing ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">{t("analytics.widgets.available")}</h3>
          <div className="flex flex-wrap gap-2">
            {WIDGET_IDS.filter((w) => !editing.includes(w)).map((w) => (
              <Button
                key={w}
                size="sm"
                variant="secondary"
                onClick={() => setEditing(addWidget(editing, w))}
              >
                {`${t("analytics.widgets.add")}: ${t(`analytics.widgets.names.${w}`)}`}
              </Button>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
